import React from 'react';
import { Grid2, Stack } from '@mui/material';
import {
    breakpoints,
    Heading,
    Text,
    v2Colors,
    Container,
    V2Button,
    typographyStyles,
} from '@web-for-marketing/react-ui';
import { BlogArticle } from '@models/blog';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { getTranslation } from '@helpers/languageTranslations';
import { useTranslation } from '@stateManagement/TranslationContext';
import { Picture } from '@components/Picture';
import { useAtom } from 'jotai';
import { selectedLanguageAtom } from '@atoms/appSettings';
import { getFormattedLocalDateTime } from '@helpers/dates';
import { CategoryPill } from '@components/BlogCards/CategoryPill';
import { languageRouteTypes } from '@helpers/languageRoutes';
import { ensureInternalURLTrailingSlash } from '@helpers/string';
import { getLanguageRouteAndPrefixByCodeAndRouteKey } from '@helpers/languages';
import { PublicDateTime } from '@components/PublicDateTime';
import { ClassNames } from '@emotion/react';

interface BlogHeaderProps {
    blogPost: BlogArticle;
    categoryBlogLink: string;
}

const classes = {
    blogHeaderContainer: {
        padding: '5.6rem 6.4rem',
        borderRadius: '.8rem .8rem 0 0',
        backgroundColor: v2Colors.background.white,
        [`@media (max-width: ${breakpoints.lg})`]: {
            padding: '5.6rem 0',
        },
        [`@media (max-width: ${breakpoints.md})`]: {
            padding: '2.4rem 0',
        },
    },
    blogTitleContainer: {
        margin: '1.6rem  0',
    },
    blogCategoryPill: {
        borderRadius: '10rem',
        borderColor: v2Colors.border.primary,
        color: v2Colors.text.secondary,
        fontWeight: 400,
    },
    picture: {
        display: 'block',
    },
    bannerImage: {
        borderRadius: '.8rem',
    },
    gridContainer: {
        boxSizing: 'border-box',
        backgroundColor: v2Colors.background.light,
        [`@media (max-width: ${breakpoints.lg})`]: {
            backgroundColor: v2Colors.background.white,
        },
    },
    title: {
        marginBottom: '1rem',
    },
    flexContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    ellipseIcon: {
        margin: ' 0 1.2rem',
        display: 'flex',
        alignItems: 'center',
        color: v2Colors.text.tertiary,
        fontSize: '2rem',
        [`@media (max-width: ${breakpoints.sm})`]: {
            margin: '0 .8rem',
        },
    },
    media: {
        height: '5rem',
        width: '5rem',
        borderRadius: '50%',
        marginRight: '1.6rem',
        objectFit: 'cover',
    },
    datesContainer: {
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    dividerIcon: {
        ...typographyStyles.body2,
        margin: '0 5px',
    },
} as const;

export const BlogHeader = ({ blogPost, categoryBlogLink }: BlogHeaderProps): JSX.Element => {
    const { prefixStringWithBaseUrl } = useStaticValues();
    const { selectedLanguageTranslation } = useTranslation();
    const [selectedLanguage] = useAtom(selectedLanguageAtom);

    const {
        author,
        category,
        title,
        description,
        publishedDateUtc,
        modifiedDateUtc,
        readMinutes,
        imageAlt,
        imageUrl,
        mobileImageUrl,
    } = blogPost;
    const readMinuteText = getTranslation(selectedLanguageTranslation, 'blogInfo', 'readMinutes').replace(
        '5',
        readMinutes ? readMinutes : '~1'
    );

    const renderedPublishedDate = getFormattedLocalDateTime(publishedDateUtc, 'll', selectedLanguage);
    const renderedModifiedDate = getFormattedLocalDateTime(modifiedDateUtc, 'll', selectedLanguage);

    return (
        <Container css={classes.gridContainer}>
            <Grid2 container spacing={{ xs: 2, md: 4, lg: 7 }} css={classes.blogHeaderContainer}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <CategoryPill category={category.name} categoryLink={`${categoryBlogLink}${category.slug}`} />
                    <div css={classes.blogTitleContainer}>
                        <Heading variant='h2' component='h1' css={classes.title}>
                            {title}
                        </Heading>
                        {description ? (
                            <Text variant='h4' weight='medium' color='secondary'>
                                {description}
                            </Text>
                        ) : null}
                    </div>
                    <div css={classes.flexContainer}>
                        <img
                            alt={author.image && author.name}
                            src={prefixStringWithBaseUrl(author.image)}
                            css={classes.media}
                        />
                        <Stack spacing='4px'>
                            <Text
                                variant='body2'
                                weight='bold'
                                color='primary'
                                css={{ display: 'flex', alignItems: 'center' }}
                            >
                                {getTranslation(selectedLanguageTranslation, 'blogInfo', 'by')}
                                &nbsp;
                                {blogPost.visible ? (
                                    <V2Button
                                        variant='link'
                                        component='a'
                                        href={ensureInternalURLTrailingSlash(
                                            `${getLanguageRouteAndPrefixByCodeAndRouteKey(
                                                selectedLanguage,
                                                languageRouteTypes.AUTHOR
                                            )}${blogPost.author.slug}`
                                        )}
                                        aria-label={`View ${author.name}'s profile`}
                                    >
                                        {author.name}
                                    </V2Button>
                                ) : (
                                    <span css={{ color: v2Colors.text.activePrimary }}>{author.name}</span>
                                )}
                            </Text>
                            <Stack direction='row' css={classes.datesContainer}>
                                <PublicDateTime>
                                    <Text variant='body2' color='tertiary'>
                                        {renderedPublishedDate}
                                    </Text>
                                </PublicDateTime>
                                {renderedPublishedDate !== renderedModifiedDate ? (
                                    <>
                                        <div aria-hidden css={classes.dividerIcon}>
                                            &#124;
                                        </div>
                                        <PublicDateTime>
                                            <Text variant='body2' color='tertiary'>
                                                Updated: {renderedModifiedDate}
                                            </Text>
                                        </PublicDateTime>
                                    </>
                                ) : null}
                            </Stack>
                            <Text variant='body2' color='tertiary'>
                                {readMinuteText}
                            </Text>
                        </Stack>
                    </div>
                </Grid2>
                {imageUrl || mobileImageUrl ? (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <ClassNames>
                            {({ css }) => (
                                <Picture
                                    lazyLoad={false}
                                    imagePath={imageUrl || mobileImageUrl}
                                    mobilePath={mobileImageUrl || imageUrl}
                                    alt={imageAlt ? imageAlt : ''}
                                    css={classes.bannerImage}
                                    pictureProps={{ className: css(classes.picture) }}
                                    aspectRatio='16/9'
                                />
                            )}
                        </ClassNames>
                    </Grid2>
                ) : null}
            </Grid2>
        </Container>
    );
};
